/**
 * 需要調達計画詳細 - 合計・需給バランス算出と前回差分判定の純関数。
 *
 * 未入力集約規則（鉄則）:
 *   - 構成内訳セルが 1 つでも空（undefined）なら合計も undefined（空）
 *   - 全て入力済み（0 含む）のときのみ合算
 *   - 需給バランスは両合計が数値のときのみ算出
 *
 * 値は number（kWh 整数）または undefined（未入力）で表現する。
 * bigint（proto PlanSlot.valueKwh）→ number 変換はこの層で行う。
 *
 * 位置づけ（SoT）: 合計は proto に乗らず提出もされない（model.proto 参照）。canonical な
 * 集計は backend の OCCTO 提出時集計であり、本層はセル編集中のライブプレビューを backend
 * ルールにミラーするに留める。0 と空（未入力）の集計セマンティクス（空の必須列を合計に
 * 含めるか等）の最終確定は残論点 #1099。合計の入力集合（空列の除外）は呼び出し側
 * （index.tsx nonEmptyColumnSlots）で明示する。
 */
import type { ProcurementPlanValueRow } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

/** 1 コマ（30分）の値。undefined = 未入力、number = 入力済（0 含む） */
export type SlotValue = number | undefined

/**
 * bigint | undefined（proto PlanSlot.valueKwh）を SlotValue（number | undefined）に変換する。
 * proto の optional int64 は bigint で表現される。
 */
export function toSlotValue(kwh: bigint | undefined): SlotValue {
  if (kwh === undefined) return undefined
  return Number(kwh)
}

/**
 * 48 コマの合計を算出する。
 * 1 つでも未入力（undefined）があれば undefined を返す（鉄則：空を 0 に潰さない）。
 */
export function calcSlotTotal(
  columns: SlotValue[][],
  komaIndex: number,
): SlotValue {
  if (columns.length === 0) return undefined
  let total = 0
  for (const col of columns) {
    const v = col[komaIndex]
    if (v === undefined) return undefined
    total += v
  }
  return total
}

/**
 * 1 列分の全コマ合計配列を算出する。
 * allColumns は各取引先列の 48 スロット配列の配列。
 */
export function calcColumnTotals(allColumns: SlotValue[][]): SlotValue[] {
  return Array.from({ length: 48 }, (_, i) => calcSlotTotal(allColumns, i))
}

/**
 * 需給バランスを算出する（spec AC-006: 調達計画値合計 − 需要計画値 − 販売計画値合計、
 * `domain-knowledge.md §5` 準拠）。いずれかが未入力なら undefined を返す。
 *
 * 発電販売計画（`発電計画合計 + 調達計画合計 − 販売計画合計`、
 * GenerationSalesPlanDetail/models/calc.ts）とは計算軸が異なる（本画面は需要調達計画なので
 * 発電の項が無く、代わりに需要の項を減算する）。
 */
export function calcSupplyDemandBalance(
  procurementTotal: SlotValue,
  demandTotal: SlotValue,
  salesTotal: SlotValue,
): SlotValue {
  if (
    procurementTotal === undefined ||
    demandTotal === undefined ||
    salesTotal === undefined
  ) {
    return undefined
  }
  return procurementTotal - demandTotal - salesTotal
}

/**
 * ProcurementPlanValueRow の slots を SlotValue[] に変換する。
 *
 * 詳細画面は read-only の「生成済み」計画値を表示する（編集バッファは廃止）。生成
 * された内訳行は全コマが確定済みで、proto に値が無いコマ（worker が option.None で出す
 * JEPX 約定未配線コマ / 相対の取引なしコマ）は業務的に「取引なし = 0」を意味する。
 * したがって表示用 SlotValue では未設定コマを 0 に倒す（"-"（cell.empty）にしない）。
 * 需要行は別経路（index.tsx slotsToValues）なので本変換の 0 倒しの影響を受けない。
 */
export function rowToSlots(
  row: Pick<ProcurementPlanValueRow, 'slots'>,
): SlotValue[] {
  return row.slots.map((s) => toSlotValue(s.valueKwh) ?? 0)
}

/**
 * 前回差分判定: current と previous のコマ値が異なるかを判定する。
 *
 * 比較ルール:
 *   - current === previous なら false（差分なし）
 *   - どちらかが undefined で他方が数値なら true（差分あり）
 *   - 両方 undefined なら false（差分なし）
 */
export function isDiffCell(current: SlotValue, previous: SlotValue): boolean {
  return current !== previous
}

/**
 * 需給バランス列の前回差分フラグ (AC-PPD-020)。前回値があり、今回の値と違うコマだけ true。
 *
 * [[isDiffCell]] (前回 undefined・今回が数値でも true) と違い、前回値が無いコマは
 * 変わったとしない (mock-app `PlanSubmissionPeriodMath.IsChanged`)。前回の需給バランスが
 * undefined になるのは前回の需要計画値が欠けた時だけで、未入力コマのある版は提出できない
 * (AC-PLC-028) ため、前回受理値では実際には起きない。
 */
export function calcBalanceDiffFlags(
  balances: readonly SlotValue[],
  previousBalances: readonly SlotValue[],
): boolean[] {
  return balances.map(
    (v, i) => previousBalances[i] !== undefined && v !== previousBalances[i],
  )
}

/**
 * 列キー（取引先別内訳の一致判定に使用）。
 * sourceType + counterpartyCode + sourceContractId で列を特定する（spec
 * procurement-plan.md L67: 「取引毎」は契約単位で別列にする。source_contract_id
 * は BFF が backend の trading_contract 単位行から forward する識別子
 * (#6236 で旧 source_leg_id から rename、trading_contract.id を指す)。
 * 同一取引先の複数契約も source_contract_id で区別できる（contractId では
 * 区別不可）。issue #5068）。
 */
export type ColumnKey = {
  sourceType: number
  counterpartyCode: string | undefined
  sourceContractId: string | undefined
}

export function makeColumnKey(col: ColumnKey): string {
  return `${col.sourceType}|${col.counterpartyCode ?? ''}|${col.sourceContractId ?? ''}`
}

/**
 * 現在の列と前回の列（同じ列の前回提出の 48 スロット）を比べ、
 * 各コマで差分があるかの boolean[48] を返す。
 * 前回に対応列が無い時に前回値 0 とするのは呼び出し側（buildBreakdownColumns の
 * previousSlotsFor）で、ここは渡された前回値と比べるだけ。前回のコマが未入力なら 0 と比べる。
 */
export function calcColumnDiff(
  currentSlots: SlotValue[],
  previousSlots: SlotValue[],
): boolean[] {
  return currentSlots.map((v, i) => isDiffCell(v, previousSlots[i] ?? 0))
}
